import {GroupData} from '@/types/group';
import {Grid, Box, Typography, useTheme, Tabs, Tab, Badge} from '@mui/material';
import {CardMediaComponent} from '@/components/ui/card-media';
import {useTranslation} from 'react-i18next';
import {Person} from '@mui/icons-material';
import PlaceIcon from '@mui/icons-material/Place';
import {isDocumentCompanyRoleHealthManager, useTitleStyle} from '@/utils/';
import {Sport} from '@/types/common';
import {useAppSelector} from '@/redux-store/store';
import Linkify from 'linkify-react';
import {useCallback, useState} from 'react';
import {fetchOtherUserData} from '@/rest/user/fetch-other-user-data';
import UserOverview, {UserInOverview} from '@/components/user/overview';
import LockIcon from '@mui/icons-material/Lock';
import {env} from '@/config';
import Chat from '@/components/matrix-chat';
import NoMemberInfo from '@/components/matrix-chat/ui/no-member-info';
import ResponsiveTypography from '@/components/ui/responsive-typography';
import {useMatrixUnreadEventsContext} from '@/contexts/matrix/unread-events';
import DocumentShare from '@/components/ui/document-share';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';

const {CONTENT_SERVER} = env;

interface GroupInfoProps {
  selectedGroup: GroupData;
  isUserAuthorizedToSeeRoom: boolean;
  onUserOverviewOpenChange: (isOpen: boolean) => void;
  defaultChatTab?: boolean;
  trainerInSjOne?: boolean;
  isUserManager?: boolean;
  companyName?: string;
}

const groupsImage = `${CONTENT_SERVER}assets/sports/team.jpg`;

const getImageSource = (group: GroupData, sport: Sport | null): string => {
  if (group.picture) {
    return `${group.picture}?timestamp=${group.updatedAt}`;
  }
  if (group.sports && group.sports.length > 1) {
    return groupsImage;
  }
  return sport?.picture || groupsImage;
};

const GroupInfo: React.FC<GroupInfoProps> = ({
  selectedGroup,
  isUserAuthorizedToSeeRoom,
  onUserOverviewOpenChange,
  defaultChatTab = false,
  trainerInSjOne,
  isUserManager,
  companyName
}) => {
  const {t} = useTranslation();

  const theme = useTheme();

  const {sports} = useAppSelector((state) => state.sports);

  const {unreadEventsCount} = useMatrixUnreadEventsContext();

  const [userOverviewDialogOpen, setUserOverviewDialogOpen] = useState(false);
  const [selectedUserToOverview, setSelectedUserToOverview] =
    useState<UserInOverview | null>(null);
  const [tabValue, setTabValue] = useState(defaultChatTab ? 1 : 0);

  const trainerInSjOneNoManager = trainerInSjOne && !isUserManager;

  const isHealthManagerGroup =
    selectedGroup && isDocumentCompanyRoleHealthManager(selectedGroup);

  const allowedToJoin =
    !trainerInSjOne ||
    (trainerInSjOne && (isHealthManagerGroup || isUserManager));

  const hostedByText = trainerInSjOneNoManager
    ? `${t('hosted_for')} ${companyName}`
    : `${t('hosted_by')} ${selectedGroup.createdBy?.name || t('deleted_user')}`;

  const handleUserOverviewClick = useCallback(
    async (userId: string) => {
      try {
        if (!trainerInSjOneNoManager) {
          const fetchedUser = await fetchOtherUserData(userId);
          setSelectedUserToOverview(fetchedUser);
          setUserOverviewDialogOpen(true);
          onUserOverviewOpenChange(true);
        }
      } catch (error) {
        console.error('Failed to fetch user', error);
        throw error;
      }
    },
    [onUserOverviewOpenChange, trainerInSjOneNoManager]
  );

  const handleUserOverviewClose = useCallback(() => {
    setSelectedUserToOverview(null);
    setUserOverviewDialogOpen(false);
    onUserOverviewOpenChange(false);
  }, [onUserOverviewOpenChange]);
  const handleTabChange = useCallback(
    (_: React.ChangeEvent<unknown>, newValue: number) => {
      setTabValue(newValue);
    },
    []
  );

  const cityName = selectedGroup?.locationName
    ? selectedGroup.locationName.split(',')[1] || ''
    : '';

  let sport = null;
  if (selectedGroup.sports && selectedGroup.sports.length > 0) {
    sport = sports[selectedGroup.sports[0]._id];
    if (!sport) {
      console.warn(`No sport found with id ${selectedGroup.sports[0]._id}`);
    }
  }

  const imageSource = getImageSource(selectedGroup, sport);

  const createdByStyle = useTitleStyle('26ch', undefined, 'md');

  return (
    <Grid container spacing={2} direction={{xs: 'column', sm: 'row'}}>
      <Grid item xs={12}>
        <Box marginBottom={2}>
          <Typography variant="h6" component="h3" sx={{textAlign: 'center'}}>
            {t('group_detail_screen')}:
          </Typography>
        </Box>
        <Box marginBottom={2}>
          <CardMediaComponent image={imageSource} altText="Group header" />
        </Box>
      </Grid>
      <Grid
        container
        item
        xs={12}
        md={12}
        spacing={2}
        direction={{xs: 'column', sm: 'row'}}
      >
        <Grid item xs={12}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            centered
            TabIndicatorProps={{
              style: {backgroundColor: theme.palette.text.primary}
            }}
          >
            <Tab
              label={
                <ResponsiveTypography
                  sx={{
                    color: theme.palette.text.primary,
                    paddingLeft: {xs: 1, sm: 4},
                    paddingRight: {xs: 1, sm: 4}
                  }}
                >
                  {t('group_details')}
                </ResponsiveTypography>
              }
            />
            {allowedToJoin && (
              <Tab
                label={
                  <Badge
                    badgeContent={unreadEventsCount}
                    color="primary"
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right'
                    }}
                  >
                    <ResponsiveTypography
                      sx={{
                        color: theme.palette.text.primary,
                        paddingLeft: 4,
                        paddingRight: 4
                      }}
                    >
                      {t('chat')}
                    </ResponsiveTypography>
                  </Badge>
                }
              />
            )}
          </Tabs>
        </Grid>

        {tabValue === 0 && (
          <Grid item xs={12} md={12}>
            <Box marginBottom={2}>
              <ResponsiveTypography medium truncateText>
                {selectedGroup.title}
              </ResponsiveTypography>
            </Box>
            <Box marginBottom={2}>
              <Typography variant="body1" style={{whiteSpace: 'pre-line'}}>
                <Linkify
                  options={{
                    target: '_blank',
                    rel: 'noopener noreferrer',
                    attributes: {
                      style: {
                        color: theme.palette.customColors?.urlLink
                      }
                    }
                  }}
                >
                  {selectedGroup.description}
                </Linkify>
              </Typography>
            </Box>

            {selectedGroup.createdBy && (
              <Box marginBottom={2}>
                <Typography
                  variant="body1"
                  component="h3"
                  sx={{
                    ...createdByStyle,
                    cursor: trainerInSjOneNoManager ? 'default' : 'pointer'
                  }}
                  onClick={() =>
                    handleUserOverviewClick(selectedGroup.createdBy?._id)
                  }
                >
                  <Person fontSize="inherit" sx={{marginRight: '12px'}} />{' '}
                  {hostedByText}
                </Typography>
              </Box>
            )}
            {cityName && (
              <Box
                sx={{
                  marginBottom: selectedGroup.accessType === 'PRIVATE' ? 0 : 2
                }}
                display="flex"
                alignItems="center"
              >
                <Typography variant="body1">
                  <PlaceIcon fontSize="inherit" sx={{marginRight: '12px'}} />{' '}
                  {cityName}
                </Typography>
              </Box>
            )}
            {selectedGroup.trainerGroup && (
              <Typography variant="body1" marginTop={2}>
                <SportsBasketballIcon
                  fontSize="inherit"
                  sx={{marginRight: '16px'}}
                />
                {t('trainer_group')}
              </Typography>
            )}
            {selectedGroup.accessType &&
              selectedGroup.accessType === 'PRIVATE' && (
                <Box marginBottom={2} display="flex" alignItems="center">
                  <Typography variant="body1" marginTop={2}>
                    <LockIcon fontSize="inherit" sx={{marginRight: '12px'}} />{' '}
                    {t('group_private')}
                  </Typography>
                </Box>
              )}
            {!trainerInSjOneNoManager && (
              <DocumentShare document={selectedGroup} documentType="group" />
            )}
          </Grid>
        )}
        {tabValue === 1 && (
          <Grid item xs={12} md={12}>
            <Box
              sx={{
                //maxWidth: '400px',
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%'
              }}
            >
              {isUserAuthorizedToSeeRoom ? (
                <Chat />
              ) : (
                <NoMemberInfo componentType={'group'} />
              )}
            </Box>
          </Grid>
        )}
      </Grid>
      {selectedUserToOverview && (
        <UserOverview
          userData={selectedUserToOverview}
          userViewDialogOpen={userOverviewDialogOpen}
          onClose={handleUserOverviewClose}
        />
      )}
    </Grid>
  );
};

export default GroupInfo;
